import { DAY, TIME } from './classNameStem';

// Rule-seed transform (S5b) -- the INITIAL team_name_pattern offered when a steward opens the
// reusable-rule dialog from a class. Distinct from classNameStem() on purpose:
//
//   classNameStem   = a GROUPING KEY. Deletes day/time tokens from anywhere and glues the edges, so
//                     schedule variants of one class type collapse together. Its output is a
//                     normalized key that is frequently NOT a contiguous substring of the name.
//   classNamePattern = a MATCHING SEED. Production matches rules with an unescaped
//                     LIKE '%' + pattern + '%', so a seed is only useful if it is a CONTIGUOUS
//                     SUBSTRING of the class name. That is this function's hard invariant.
//
// Measured 2026-08-11 over the 1,417 distinct (com_code, class name) pairs in the TY2026 unassigned
// population, matched with production-equivalent LIKE + CI_AS collation + franchise scope:
//   * the stem matches ZERO classes for 60.0% of candidates (843 of the 864 whose stem is glued);
//   * the contiguous prefix matches zero for only 2.0%;
//   * prefix seeding sweeps a DIFFERENT level signature in 2.0% of glued candidates, and that risk
//     is predictable -- it concentrates where a day token sits BEFORE the level tokens
//     ("Poway - GIRLS Monday Skills Lvl.2 (6-8)" truncates to "Poway - GIRLS"). Guarding on
//     level-token retention (rule 4 below) leaves 1.5% residual, all of it peers whose own names
//     carry extra tokens -- which no prefix rule can prevent.
//
// DELIBERATELY NOT DONE (Tim, 2026-08-11): no wildcard ('%') seeding. Wildcard preserves specificity
// by pinning field/room designators, so it cannot express a recurring class type at all -- 37.7% of
// prefix families would need more than one wildcard rule and `danvers` would need 20 where a prefix
// needs 1. Introducing '%' would also add a new vocabulary to a rule set whose 162 active patterns
// are all literal substrings. That stays a separate rule-language decision.
//
// KNOWN, OUT OF SCOPE: production treats patterns as RAW LIKE patterns, so a '_' or '[' already in a
// class name is already a live wildcard (e.g. beaumont's "Micro_Saturday_10:00AM (4/5)"). This
// function neither introduces nor escapes them; escaping is its own slice.

// Tokens that distinguish one level from another: age ranges ("4-5", "5/6", "(6-8)"), ages with a
// unit ("5 yrs", "30-42mo"), explicit level markers ("L1", "Lvl.2", "Level One"), and the
// progression words. Deliberately GENEROUS: a false hit costs only a fallback to the full class
// name, never a broader pattern, so the error direction is always the safe one. (Measured example
// of that trade: scor's "Micro 6-7 Mon 4:15 - starts 4/6" reads the DATE "4/6" as an age range and
// falls back, which is a lost convenience, not a bad rule.)
const LEVEL_BEARING = /\b\d{1,2}\s*[-/]\s*\d{1,2}\b|\b\d{1,3}\s*(?:yrs?|years?|mos?|months?)\b|\b(?:l|lvl|level)\s*\.?\s*(?:one|two|three|four|[1-4])\b|\b(?:advanced|adv|beginner|beg|intermediate|int)\b/gi;

// DAY/TIME/LEVEL_BEARING all carry /g, so exec() would leave lastIndex set and leak into the next
// call. Every use builds a fresh instance from the shared source instead.
const fresh = (re) => new RegExp(re.source, 'gi');

// Offset of the first removable schedule token, or -1 when the name carries none.
function firstScheduleTokenStart(padded) {
  let first = -1;
  for (const re of [fresh(DAY), fresh(TIME)]) {
    const m = re.exec(padded);
    if (m && m[0].length && (first === -1 || m.index < first)) first = m.index;
  }
  return first;
}

// Normalized set of level-bearing tokens in a string ("Lvl. 2" and "lvl2" are the same token).
function levelTokens(text) {
  const re = fresh(LEVEL_BEARING);
  const found = new Set();
  let m;
  while ((m = re.exec(text)) !== null) {
    if (!m[0].length) { re.lastIndex += 1; continue; }
    found.add(m[0].toLowerCase().replace(/[\s.]+/g, ''));
  }
  return found;
}

// Returns the seed pattern for a class name. GUARANTEED to be a contiguous substring of the trimmed
// input (or '' for an empty input) -- callers may rely on that; the preview is still the
// authoritative reach check.
export function classNamePattern(name) {
  if (name === null || name === undefined) return '';
  const original = String(name).trim();
  if (!original) return '';

  // Same padding classNameStem uses, so the \b anchors behave identically at the edges.
  const padded = ` ${original} `;
  const start = firstScheduleTokenStart(padded);
  if (start === -1) return original;              // nothing removable -> the whole name is the seed

  const prefix = padded.slice(0, start).trim();
  if (!prefix) return original;                   // the name LEADS with its schedule -> no prefix

  // The guard. If truncating here would drop level-bearing information that the full name carries,
  // the prefix would silently claim sibling classes at OTHER levels. Fall back to the full name and
  // let the steward widen it deliberately -- never broaden on their behalf.
  const inPrefix = levelTokens(prefix);
  for (const token of levelTokens(original)) {
    if (!inPrefix.has(token)) return original;
  }

  // Invariant backstop: the contract above is what callers rely on, so verify rather than assume.
  return original.includes(prefix) ? prefix : original;
}
